import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { Color, IconName } from '../../enums';
import { UseState } from '../../types';
import { Icon } from '../icon/icon.component';

import { ArrowButton, ArrowsContainer, Container, ValuesContainer } from './pagination.styled';
import { PaginationProps } from './pagination.types';

export const Pagination: React.FC<PaginationProps> = (props: PaginationProps): JSX.Element => {
  const { className, maxItems, itemsPerPage = 10, onPageChange, initialPageCount }: PaginationProps = props;
  const [page, setPage]: UseState<number> = useState<number>(initialPageCount ?? 1);
  const maxValuePerPage: number = useMemo((): number => (itemsPerPage * page), [page, itemsPerPage]);
  const minValuePerPage: number = useMemo((): number => (maxValuePerPage - itemsPerPage) + 1, [maxValuePerPage, itemsPerPage]);
  const maxPage: number = Math.ceil(maxItems / itemsPerPage);
  const { t }: TransProps<never> = useTranslation();

  const setNextPage: () => void = (): void => {
    if (page < maxPage) {
      onPageChange(page + 1);
      setPage(page + 1);
    }
  };

  const setPreviousPage: () => void = (): void => {
    if (page > 1) {
      onPageChange(page - 1);
      setPage(page - 1);
    }
  };

  useEffect(
    (): void => {
      if (initialPageCount && initialPageCount !== page) {
        setPage(initialPageCount);
      }
    },
    [initialPageCount]
  );

  useEffect(
    (): void => {
      if (page !== 1 && !initialPageCount) {
        onPageChange(1);
        setPage(1);
      }
    },
    [maxItems, initialPageCount]
  );

  return (
    <Container className={className} data-testid='pagination'>
      <ValuesContainer>
        {t('onarte.ui.pagination.label', {
          items: `${page === 1 ? 1 : minValuePerPage}-${maxValuePerPage >= maxItems ? maxItems : maxValuePerPage}`,
          maxItems,
        })}
      </ValuesContainer>
      <ArrowsContainer>
        <ArrowButton onClick={setPreviousPage} disabled={page <= 1}>
          <Icon name={IconName.ArrowLeft15} size={16} color={page <= 1 ? Color.Gray400 : Color.Black}/>
        </ArrowButton>
        <ArrowButton onClick={setNextPage} disabled={page === maxPage} aria-label='right-arrow'>
          <Icon name={IconName.ArrowRight15} size={16} color={page === maxPage ? Color.Gray400 : Color.Black}/>
        </ArrowButton>
      </ArrowsContainer>
    </Container>
  );
};
